import Papa from 'papaparse';
import { Timestamp } from 'firebase/firestore';
import { Lead, LeadUpdateEntry } from '../types/lead.types';
import { CsvImportTemplate, ParsedImportRow } from '../types/csvTemplate.types';
import { updateLead } from './lead.service';

// Recursively drop `undefined` values — Firestore rejects them anywhere in a
// document (including nested in arrays/objects). Preserves Timestamps.
function stripUndefinedDeep(v: any): any {
  if (Array.isArray(v)) return v.map(stripUndefinedDeep);
  if (v && typeof v === 'object' && typeof v.toDate !== 'function') {
    const out: any = {};
    for (const [k, val] of Object.entries(v)) {
      if (val === undefined) continue;
      out[k] = stripUndefinedDeep(val);
    }
    return out;
  }
  return v;
}

export interface ImportMatchResult {
  parsed: ParsedImportRow;
  status: 'matched' | 'ambiguous' | 'unmatched';
  leadId?: string;            // chosen/sole match
  candidateLeadIds: string[]; // populated when ambiguous
  changes: { field: string; from?: string; to?: string }[]; // real diffs vs current lead
  matchedBy?: 'hubspotId' | 'name';
  protectedValid?: boolean;   // recap tried to downgrade a valid lead — blocked
}

export interface ImportPreview {
  template: CsvImportTemplate;
  headerErrors: string[];     // missing required headers
  results: ImportMatchResult[];
  skippedBlank: number;
}

export interface ImportApplySummary {
  updated: number;
  noChange: number;
  failed: number;
  protectedValid: number; // downgrades of valid leads blocked by the client rule
}

// ---- helpers --------------------------------------------------------------

const normName = (first?: string, last?: string): string =>
  `${(first || '').trim().toLowerCase()} ${(last || '').trim().toLowerCase()}`.trim();

const leadNormName = (lead: Lead): string => {
  if (lead.parentFirstName || lead.parentLastName) {
    return normName(lead.parentFirstName, lead.parentLastName);
  }
  const parts = (lead.parentName || '').trim().split(/\s+/);
  return normName(parts[0], parts.slice(1).join(' '));
};

const fmtDate = (v: any): string => {
  try {
    if (v && typeof v.toDate === 'function') return v.toDate().toISOString().split('T')[0];
  } catch { /* ignore */ }
  return '';
};

// Fields the recap can change. lastContacted is compared by calendar date.
const TRACKED_FIELDS: (keyof Lead)[] = [
  'determination', 'status', 'hubspotFunnel', 'hubspotContactType', 'hubspotContactId', 'lastContacted',
];

// Client rule: once ANY client export marks a lead Valid, it stays valid —
// validated work may already be billed, so a later recap flipping it to
// Invalid/Pending is ignored (the rest of the row still applies).
const protectValidDetermination = (
  lead: Lead,
  updates: Partial<Lead>
): { updates: Partial<Lead>; protectedValid: boolean } => {
  if (lead.determination === 'valid' && updates.determination && updates.determination !== 'valid') {
    const { determination, ...rest } = updates;
    return { updates: rest, protectedValid: true };
  }
  return { updates, protectedValid: false };
};

const computeChanges = (lead: Lead, updates: Partial<Lead>) => {
  const changes: { field: string; from?: string; to?: string }[] = [];
  for (const field of TRACKED_FIELDS) {
    if (!(field in updates)) continue;
    const toRaw = (updates as any)[field];
    const fromRaw = (lead as any)[field];
    const toStr = field === 'lastContacted' ? fmtDate(toRaw) : String(toRaw ?? '');
    const fromStr = field === 'lastContacted' ? fmtDate(fromRaw) : String(fromRaw ?? '');
    if (toStr !== fromStr) {
      changes.push({ field: String(field), from: fromStr || undefined, to: toStr || undefined });
    }
  }
  return changes;
};

export const leadImportService = {
  /** Parse uploaded CSV text into a row-by-row preview matched against the
   *  given project leads. Pure — applies nothing. */
  parseAndMatch(csvText: string, template: CsvImportTemplate, leads: Lead[]): ImportPreview {
    const parsed = Papa.parse<Record<string, string>>(csvText, {
      header: true,
      skipEmptyLines: 'greedy',
      transformHeader: (h) => h.trim(),
    });

    const present = new Set((parsed.meta.fields || []).map(f => f.trim()));
    const headerErrors = template.requiredHeaders.filter(h => !present.has(h));

    // Indexes for matching.
    const byHubspotId = new Map<string, Lead>();
    const byName = new Map<string, Lead[]>();
    for (const lead of leads) {
      if (lead.hubspotContactId) byHubspotId.set(lead.hubspotContactId.trim(), lead);
      const n = leadNormName(lead);
      if (n) (byName.get(n) || byName.set(n, []).get(n)!).push(lead);
    }

    const results: ImportMatchResult[] = [];
    let skippedBlank = 0;

    (parsed.data || []).forEach((raw, i) => {
      const row = template.parseRow(raw, i + 2); // +2 → 1-based with header row
      if (!row) { skippedBlank++; return; }

      let match: ImportMatchResult = {
        parsed: row, status: 'unmatched', candidateLeadIds: [], changes: [],
      };

      for (const key of row.matchKeys) {
        if (key.kind === 'hubspotId') {
          const lead = byHubspotId.get(key.value.trim());
          if (lead) {
            const safe = protectValidDetermination(lead, row.updates);
            match = {
              parsed: row, status: 'matched', leadId: lead.id, candidateLeadIds: [lead.id],
              changes: computeChanges(lead, safe.updates), matchedBy: 'hubspotId',
              protectedValid: safe.protectedValid,
            };
            break;
          }
        } else if (key.kind === 'name') {
          const candidates = byName.get(normName(key.first, key.last)) || [];
          if (candidates.length === 1) {
            const safe = protectValidDetermination(candidates[0], row.updates);
            match = {
              parsed: row, status: 'matched', leadId: candidates[0].id, candidateLeadIds: [candidates[0].id],
              changes: computeChanges(candidates[0], safe.updates), matchedBy: 'name',
              protectedValid: safe.protectedValid,
            };
            break;
          } else if (candidates.length > 1) {
            match = {
              parsed: row, status: 'ambiguous', candidateLeadIds: candidates.map(c => c.id), changes: [],
            };
            // keep scanning in case a later key (hubspotId) resolves uniquely
          }
        }
      }
      results.push(match);
    });

    return { template, headerErrors, results, skippedBlank };
  },

  /** Apply the chosen updates. `selections` maps a result index → leadId to
   *  apply (lets the UI resolve ambiguous rows or skip rows). Writes an
   *  updateHistory entry per changed lead. */
  async applyUpdates(
    preview: ImportPreview,
    leadsById: Map<string, Lead>,
    selections: Map<number, string>,
    batchId: string,
    actor?: { userId: string; userName: string }
  ): Promise<ImportApplySummary> {
    const summary: ImportApplySummary = { updated: 0, noChange: 0, failed: 0, protectedValid: 0 };

    for (let i = 0; i < preview.results.length; i++) {
      const result = preview.results[i];
      const leadId = selections.get(i) ?? (result.status === 'matched' ? result.leadId : undefined);
      if (!leadId) continue;

      const lead = leadsById.get(leadId);
      if (!lead) { summary.failed++; continue; }

      // Enforce the valid-stays-valid client rule at apply time too (covers
      // ambiguous/unmatched rows resolved via manual selection).
      const safe = protectValidDetermination(lead, result.parsed.updates);
      if (safe.protectedValid) summary.protectedValid++;

      const changes = computeChanges(lead, safe.updates);
      if (changes.length === 0) { summary.noChange++; continue; }

      const entry: LeadUpdateEntry = {
        timestamp: Timestamp.now(),
        source: preview.template.source,
        changes,
        batchId,
      };

      // Append a human-readable note to the lead so the client's update shows
      // in the notes timeline, not just structured history.
      const dateStr = new Date().toISOString().split('T')[0];
      const noteLine = `[${preview.template.source} ${dateStr}] ` +
        changes.map(c => `${c.field}: ${c.from ? `${c.from} → ` : ''}${c.to ?? '(cleared)'}`).join('; ');
      const newNotes = lead.notes ? `${lead.notes}\n${noteLine}` : noteLine;

      try {
        // Firestore rejects `undefined` anywhere in the doc (including nested
        // in updateHistory entries, e.g. a change with no prior value). Deep-
        // strip before writing.
        // When the recap marks a family Enrolled for the FIRST time, default
        // each of its students to enrolled too — the client reports enrollment
        // per family, not per student. Staff can later un-mark any student who
        // did not actually enroll (Lead Details → per-child Status), so the
        // per-student enrollment count stays accurate for partial families.
        // Only on the transition (lead not already enrolled) so a re-import
        // never stomps those manual corrections.
        const cascadeEnroll =
          safe.updates.status === 'enrolled' &&
          lead.status !== 'enrolled' &&
          (lead.children || []).length > 0;
        const payload = stripUndefinedDeep({
          ...safe.updates,
          ...(cascadeEnroll
            ? { children: (lead.children || []).map((c) => ({ ...c, status: 'enrolled' as Lead['status'] })) }
            : {}),
          notes: newNotes,
          updateHistory: [...(lead.updateHistory || []), entry],
          lastUpdated: Timestamp.now(),
        });
        // Pass actor so a determination change (e.g. pending→valid from the
        // recap) is recorded in determinationChanges for billing carry-over.
        await updateLead(leadId, payload, actor?.userId, actor?.userName);
        summary.updated++;
      } catch (err) {
        console.error('Failed to apply recap update for lead', leadId, err);
        summary.failed++;
      }
    }

    return summary;
  },
};
